<template>
  <teleport to="body">
    <div class="fc-fond" @mousedown.self="$emit('fermer')">
      <div class="fc-carte card shadow" role="dialog" aria-modal="true" aria-labelledby="fc-titre">
        <div class="card-header d-flex justify-content-between align-items-center gap-2 flex-wrap">
          <h5 id="fc-titre" class="mb-0"><i class="bi bi-building me-2 text-primary"></i>{{ c.nom }}</h5>
          <div class="d-flex gap-2">
            <button class="btn btn-sm btn-outline-secondary" :disabled="chargement" @click="imprimerFiche"><i class="bi bi-printer me-1"></i>Imprimer</button>
            <button class="btn btn-sm btn-outline-secondary" :disabled="chargement" @click="pdfFiche"><i class="bi bi-file-earmark-pdf me-1"></i>PDF</button>
            <button type="button" class="btn-close ms-1" aria-label="Fermer" @click="$emit('fermer')"></button>
          </div>
        </div>
        <div class="card-body">
          <div class="d-flex flex-wrap gap-2 mb-3">
            <span class="badge" :class="classeStatut">{{ libelleStatut }}</span>
            <span v-if="c.aVerifier" class="badge bg-warning text-dark" :title="c.aVerifier">À vérifier</span>
            <span class="badge bg-light text-dark border">{{ c.origine === 'INSCRIPTION' ? 'Inscrite en ligne' : 'Ouverte par la plateforme' }}</span>
          </div>

          <div class="row g-3">
            <div v-for="s in sections" :key="s.titre" class="col-md-6">
              <div class="border rounded p-2 h-100">
                <div class="small fw-bold text-primary mb-1">{{ s.titre }}</div>
                <table class="table table-sm table-borderless mb-0 small">
                  <tbody>
                    <tr v-for="[l, v] in s.lignes" :key="l"><td class="text-muted lib">{{ l }}</td><td>{{ v }}</td></tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div class="mt-3">
            <div class="small fw-bold text-primary mb-1">Modules actifs ({{ modulesActifs.length }})</div>
            <div class="d-flex flex-wrap gap-1">
              <span v-for="m in modulesActifs" :key="m.code" class="badge bg-info-subtle text-info-emphasis border">{{ m.nom }}</span>
              <span v-if="!modulesActifs.length" class="text-muted small">Aucun module en plus du socle.</span>
            </div>
            <div class="small text-muted mt-1">Le socle (patients, consultations, ordonnances, salle d'attente, caisse) est toujours inclus.</div>
          </div>

          <div class="mt-3">
            <div class="small fw-bold text-primary mb-1">Utilisateurs</div>
            <div v-if="chargement" class="text-muted small">Chargement…</div>
            <table v-else-if="utilisateurs.length" class="table table-sm small mb-0">
              <thead><tr><th>Nom</th><th>Rôle</th><th>Dernière connexion</th><th>Statut</th></tr></thead>
              <tbody>
                <tr v-for="u in utilisateurs" :key="u.id || u.email">
                  <td>{{ u.nom }}</td><td>{{ u.role || '—' }}</td><td>{{ dateHeure(u.derniereConnexion) || 'jamais' }}</td>
                  <td>{{ u.actif === false ? 'désactivé' : 'actif' }}</td>
                </tr>
              </tbody>
            </table>
            <div v-else class="text-muted small">Aucun utilisateur.</div>
          </div>

          <div class="mt-3">
            <div class="small fw-bold text-primary mb-1">Dernières factures d'abonnement</div>
            <table v-if="factures.length" class="table table-sm small mb-0">
              <thead><tr><th>N°</th><th>Période</th><th class="text-end">Montant</th><th>Statut</th><th>Payée le</th></tr></thead>
              <tbody>
                <tr v-for="f in factures" :key="f.id">
                  <td>{{ f.numero }}</td><td>{{ f.libellePeriode }}</td><td class="text-end">{{ fcfa(f.montant) }}</td>
                  <td>{{ { EMISE: 'À payer', PAYEE: 'Payée', ANNULEE: 'Annulée' }[f.statut] || f.statut }}</td><td>{{ f.datePaiement ? dateCourte(f.datePaiement) : '—' }}</td>
                </tr>
              </tbody>
            </table>
            <div v-else-if="!chargement" class="text-muted small">Aucune facture.</div>
          </div>

          <div v-if="c.notes" class="mt-3">
            <div class="small fw-bold text-primary mb-1">Notes internes</div>
            <div class="small border rounded p-2 bg-light" style="white-space: pre-line">{{ c.notes }}</div>
          </div>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script>
import api from '@/api'
import dayjs from 'dayjs'
import { imprimer, telechargerPdf, blocInfos, e, fcfa } from '@/impression/impression'

/**
 * Fiche d'une clinique cliente (plateforme) : identité, contact, abonnement,
 * modules, utilisateurs, dernières factures. Imprimable et en PDF.
 * Le gestionnaire ne voit pas l'apporteur ni sa commission.
 */
export default {
  name: 'FicheClinique',
  props: {
    clinique: { type: Object, required: true },
    // Catalogue des modules (code → { nom, description }) chargé avec les offres
    catalogueModules: { type: Object, default: () => ({}) },
  },
  emits: ['fermer'],
  data() {
    return { chargement: true, utilisateurs: [], factures: [] }
  },
  computed: {
    c() { return this.clinique },
    principal() { return !this.$store.getters.isPlatformGestionnaire },
    libelleStatut() {
      const c = this.c
      if (c.statut === 'SUPPRIMEE') return 'Supprimée'
      if (c.statut === 'SUSPENDU') return 'Suspendue'
      if (c.joursRestants !== null && c.joursRestants !== undefined && c.joursRestants < -7) return 'Expirée'
      return { ESSAI: 'En essai', ACTIF: 'Abonnée' }[c.statut] || c.statut
    },
    classeStatut() {
      return { 'En essai': 'bg-primary', Abonnée: 'bg-success', Suspendue: 'bg-danger', Expirée: 'bg-danger', Supprimée: 'bg-secondary' }[this.libelleStatut] || 'bg-secondary'
    },
    echeance() {
      const c = this.c
      if (!c.dateFinAbonnement) return 'Sans échéance'
      const j = c.joursRestants
      const reste = j === null || j === undefined ? '' : (j >= 0 ? ` (dans ${j} j)` : ` (dépassée de ${-j} j)`)
      return this.dateCourte(c.dateFinAbonnement) + reste
    },
    modulesActifs() {
      return (this.c.modulesActifs || []).map(code => ({ code, nom: this.catalogueModules[code]?.nom || code }))
    },
    sections() {
      const c = this.c
      const a = c.activite || {}
      const vide = v => (v === null || v === undefined || v === '' ? '—' : v)
      const s = [
        { titre: 'Identité', lignes: [
          ['Nom', vide(c.nom)], ['Ville', vide(c.ville)], ['Pays', vide(c.pays)],
          ['Indicatif', vide(c.indicatifTelephone)], ['Domaine', vide(c.domaine)], ['Ouverte le', this.dateCourte(c.createdAt) || '—'],
        ] },
        { titre: 'Contact', lignes: [
          ['Responsable', vide(c.contactNom)], ['Téléphone', vide(c.contactTelephone)], ['E-mail', vide(c.contactEmail)],
        ] },
        { titre: 'Abonnement', lignes: [
          ['Offre', c.planInfo ? c.planInfo.nom : '—'], ['Facturation', c.periodicite === 'ANNUEL' ? 'Annuelle' : 'Mensuelle'],
          ['Statut', this.libelleStatut], ['Fin', this.echeance],
          ['Utilisateurs max', c.maxUtilisateurs ?? "Limite de l'offre"],
          ...(c.changementPrevu ? [['Changement prévu', `${c.changementPrevu.plan.nom} le ${this.dateCourte(c.changementPrevu.le)}`]] : []),
          ...(c.resiliation ? [['Résiliation', `fin le ${this.dateCourte(c.resiliation.echeance)}`]] : []),
        ] },
        { titre: 'Activité', lignes: [
          ['Utilisateurs actifs', String(a.utilisateurs ?? 0)], ["Actifs aujourd'hui", String(a.actifsAujourdhui ?? 0)],
          ['Actifs sur 7 jours', String(a.actifs7j ?? 0)], ['Dernière activité', a.derniereActivite ? `${this.dateHeure(a.derniereActivite)}${a.parQui ? ' — ' + a.parQui : ''}` : 'jamais'],
        ] },
      ]
      if (this.principal && c.apporteurInfo) {
        s.push({ titre: "Apporteur d'affaires", lignes: [
          ['Apporteur', c.apporteurInfo.nom], ['Commission', `${c.apporteurInfo.taux} %`], ['Depuis', this.dateCourte(c.apporteurInfo.depuis) || '—'],
        ] })
      }
      return s
    },
  },
  async created() {
    const [act, fac] = await Promise.allSettled([
      api.custom(`plateforme/cliniques/${this.c.id}/activite`, 'GET'),
      api.liste('plateforme/factures', { clinique: this.c.id, itemsperpage: 6, orderBy: 'f.id', sortOrder: 'DESC' }),
    ])
    if (act.status === 'fulfilled') {
      this.utilisateurs = (act.value.data.utilisateurs || []).map(u => ({
        id: u.id, email: u.email,
        nom: u.nom || [u.prenom, u.nomFamille].filter(Boolean).join(' ') || u.email,
        role: u.role || u.typeUser, derniereConnexion: u.derniereConnexion || u.lastLoginAt, actif: u.enabled ?? u.actif,
      }))
    }
    if (fac.status === 'fulfilled') this.factures = fac.value.data?.data || []
    this.chargement = false
  },
  methods: {
    dateCourte(d) { return d ? dayjs(d).format('DD/MM/YYYY') : '' },
    dateHeure(d) { return d ? dayjs(d).format('DD/MM/YYYY HH:mm') : '' },
    fcfa(v) { return new Intl.NumberFormat('fr-FR').format(v || 0) + ' FCFA' },
    optionsDocument() {
      const sections = this.sections.map(s => `<div class="section">${e(s.titre)}</div>${blocInfos(s.lignes, 2)}`).join('')
      const modules = this.modulesActifs.map(m => e(m.nom)).join(', ') || 'Aucun module en plus du socle'
      const utilisateurs = this.utilisateurs.length ? `
        <div class="section">Utilisateurs (${this.utilisateurs.length})</div>
        <table class="lignes"><thead><tr><th>Nom</th><th>Rôle</th><th>Dernière connexion</th><th>Statut</th></tr></thead><tbody>
        ${this.utilisateurs.map(u => `<tr><td>${e(u.nom)}</td><td>${e(u.role || '—')}</td><td>${e(this.dateHeure(u.derniereConnexion) || 'jamais')}</td><td>${u.actif === false ? 'désactivé' : 'actif'}</td></tr>`).join('')}
        </tbody></table>` : ''
      const factures = this.factures.length ? `
        <div class="section">Dernières factures d'abonnement</div>
        <table class="lignes"><thead><tr><th>N°</th><th>Période</th><th class="droite">Montant</th><th>Statut</th><th>Payée le</th></tr></thead><tbody>
        ${this.factures.map(f => `<tr><td>${e(f.numero)}</td><td>${e(f.libellePeriode)}</td><td class="droite">${e(fcfa(f.montant))}</td><td>${e({ EMISE: 'À payer', PAYEE: 'Payée', ANNULEE: 'Annulée' }[f.statut] || f.statut)}</td><td>${e(f.datePaiement ? this.dateCourte(f.datePaiement) : '—')}</td></tr>`).join('')}
        </tbody></table>` : ''
      return {
        titre: 'Fiche clinique',
        sousTitre: this.libelleStatut,
        numero: this.c.slug || String(this.c.id),
        date: dayjs().format('DD/MM/YYYY'),
        // En-tête de la plateforme, et non celui d'une clinique
        config: { nom: this.c.nom, ville: this.c.ville, telephone: this.c.contactTelephone, email: this.c.contactEmail },
        corps: `${sections}<div class="section">Modules actifs</div><p>${modules}</p>${utilisateurs}${factures}
          ${this.c.notes ? `<div class="section">Notes internes</div><div class="texte">${e(this.c.notes)}</div>` : ''}`,
        note: 'Document interne de la plateforme',
      }
    },
    imprimerFiche() { imprimer(this.optionsDocument()) },
    pdfFiche() { telechargerPdf(this.optionsDocument(), `fiche-${this.c.slug || this.c.id}.pdf`) },
  },
}
</script>

<style scoped>
.fc-fond { position: fixed; inset: 0; background: rgba(15, 23, 42, .45); z-index: 2050; display: flex; align-items: flex-start; justify-content: center; padding: 4vh 16px; overflow-y: auto; }
.fc-carte { width: 100%; max-width: 900px; border-radius: 12px; border: none; }
.lib { width: 40%; white-space: nowrap; }
</style>
